import type { AgentOrbColors } from "./orb-math"
import { parseHexColor } from "./orb-math"

const SIZE = 256

const rgba = (hex: string, alpha: number, shade = 1) => {
	const [r, g, b] = (parseHexColor(hex) ?? [0.5, 0.5, 0.5]).map((c) =>
		Math.round(Math.min(1, c * shade) * 255),
	)
	return `rgba(${r},${g},${b},${alpha})`
}

function blob(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	radius: number,
	color: string,
) {
	const g = ctx.createRadialGradient(x, y, 0, x, y, radius)
	g.addColorStop(0, color)
	g.addColorStop(1, color.replace(/[\d.]+\)$/, "0)"))
	ctx.fillStyle = g
	ctx.fillRect(0, 0, SIZE, SIZE)
}

/**
 * The picture the shader bends over the sphere: a soft, lit ball in the accent's
 * family, the way a photographed glass sphere would read through frosted glass.
 */
export function paintOrbTexture(
	canvas: HTMLCanvasElement,
	[accent, light, tint]: AgentOrbColors,
) {
	canvas.width = canvas.height = SIZE
	const ctx = canvas.getContext("2d")
	if (!ctx) return
	const s = SIZE
	ctx.fillStyle = rgba(accent, 1)
	ctx.fillRect(0, 0, s, s)
	// Saturated body with the neighbouring hue folded into its left flank.
	blob(ctx, s * 0.55, s * 0.5, s * 0.5, rgba(accent, 1, 1.12))
	blob(ctx, s * 0.08, s * 0.55, s * 0.4, rgba(tint, 0.9))
	blob(ctx, s * 0.35, s * 0.85, s * 0.25, rgba(tint, 0.5, 0.8))
	// Core shadow: a deep band across the lower middle and a darker rim at the bottom right.
	blob(ctx, s * 0.4, s * 0.62, s * 0.28, rgba(accent, 0.6, 0.5))
	blob(ctx, s * 0.88, s * 0.88, s * 0.36, rgba(accent, 0.8, 0.4))
	blob(ctx, s * 0.15, s * 0.95, s * 0.22, rgba(accent, 0.6, 0.4))
	// Light from the top right: a wide warm glow, a hot spot, and a thin streak.
	blob(ctx, s * 0.66, s * 0.24, s * 0.55, rgba(light, 0.95))
	blob(ctx, s * 0.62, s * 0.2, s * 0.16, "rgba(255,255,255,0.9)")
	blob(ctx, s * 0.25, s * 0.32, s * 0.24, rgba(light, 0.7))
	blob(ctx, s * 0.7, s * 0.6, s * 0.2, rgba(light, 0.45))
	// Bounce light along the lower rim.
	blob(ctx, s * 0.5, s * 1.02, s * 0.2, rgba(light, 0.45))
}
